import React, { useState, useEffect } from 'react';
import { tracksAPI, streamingAPI, ttsAPI, newsAPI, weatherAPI } from '../../services/api';
import './Admin.css';

const DashboardHome = () => {
  const [stats, setStats] = useState({
    totalTracks: 0,
    activeTracks: 0,
    blockedTracks: 0,
    totalDuration: 0
  });
  const [nowPlaying, setNowPlaying] = useState(null);
  const [ttsQueue, setTtsQueue] = useState({ queueLength: 0, queue: [] });
  const [loading, setLoading] = useState(true);
  const [broadcastingNews, setBroadcastingNews] = useState(false);
  const [broadcastingWeather, setBroadcastingWeather] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState(null);

  useEffect(() => {
    loadStats();
    loadNowPlaying();
    loadTtsQueue();

    // Poll now playing every 5 seconds
    const interval = setInterval(() => {
      loadNowPlaying();
      loadTtsQueue();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await tracksAPI.getAll();
      const tracks = response.data.tracks;
      
      const totalDuration = tracks.reduce((sum, track) => sum + (track.duration || 0), 0);
      
      setStats({
        totalTracks: tracks.length,
        activeTracks: tracks.filter(t => !t.blocked).length,
        blockedTracks: tracks.filter(t => t.blocked).length,
        totalDuration
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadNowPlaying = async () => {
    try {
      const response = await streamingAPI.getNowPlaying();
      setNowPlaying(response.data);
    } catch (error) {
      console.error('Failed to load now playing:', error);
    }
  };

  const loadTtsQueue = async () => {
    try {
      const response = await ttsAPI.getQueue();
      setTtsQueue(response.data);
    } catch (error) {
      console.error('Failed to load TTS queue:', error);
    }
  };

  const broadcastNewsNow = async () => {
    setBroadcastingNews(true);
    setBroadcastMessage(null);
    try {
      await newsAPI.broadcast();
      setBroadcastMessage({ type: 'success', text: 'Laatste nieuws (top 3) in de wachtrij gezet!' });
      loadTtsQueue();
    } catch (error) {
      setBroadcastMessage({ type: 'error', text: error.response?.data?.error || 'Fout bij uitzenden nieuws' });
    } finally {
      setBroadcastingNews(false);
    }
  };

  const broadcastWeatherNow = async () => {
    setBroadcastingWeather(true);
    setBroadcastMessage(null);
    try {
      await weatherAPI.broadcast();
      setBroadcastMessage({ type: 'success', text: 'Weerbericht in de wachtrij gezet!' });
      loadTtsQueue();
    } catch (error) {
      setBroadcastMessage({ type: 'error', text: error.response?.data?.error || 'Fout bij uitzenden weer' });
    } finally {
      setBroadcastingWeather(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatTotalDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getVoiceLabel = (voiceId) => {
    if (!voiceId) return 'Standaard';
    if (voiceId.includes('ronnie')) return 'Ronnie (NL)';
    if (voiceId.includes('rdh')) return 'RDH (BE)';
    if (voiceId.includes('mls')) return 'MLS';
    return voiceId.split('-').slice(1, 3).join(' ');
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  const schedule = nowPlaying?.currentSchedule;
  const nextTrack = nowPlaying?.nextTrack;

  return (
    <div className="dashboard-home">
      <header className="page-header">
        <h1>Dashboard</h1>
        <p>Overzicht van je radio statistieken</p>
      </header>

      <div className="dashboard-quick-actions">
        <button
          onClick={broadcastNewsNow}
          className="btn-primary"
          disabled={broadcastingNews}
        >
          {broadcastingNews ? (
            <><i className="fas fa-spinner fa-spin"></i> Nieuws uitzenden...</>
          ) : (
            <><i className="fas fa-newspaper"></i> Speel Laatste Nieuws Nu Af</>
          )}
        </button>
        <button
          onClick={broadcastWeatherNow}
          className="btn-primary"
          disabled={broadcastingWeather}
        >
          {broadcastingWeather ? (
            <><i className="fas fa-spinner fa-spin"></i> Weer uitzenden...</>
          ) : (
            <><i className="fas fa-cloud-sun"></i> Speel Weerbericht Nu Af</>
          )}
        </button>
        {broadcastMessage && (
          <span className={`broadcast-inline-message broadcast-inline-message--${broadcastMessage.type}`}>
            {broadcastMessage.text}
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-music"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.totalTracks}</h3>
            <p>Totaal Tracks</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.activeTracks}</h3>
            <p>Actieve Tracks</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blocked">
            <i className="fas fa-ban"></i>
          </div>
          <div className="stat-info">
            <h3>{stats.blockedTracks}</h3>
            <p>Geblokkeerde Tracks</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-info">
            <h3>{formatTotalDuration(stats.totalDuration)}</h3>
            <p>Totale Duur</p>
          </div>
        </div>
      </div>

      {/* Two-column layout: Now Playing + Upcoming */}
      <div className="radio-live-grid">

        {/* Now Playing */}
        <div className="now-playing-card">
          <h2><i className="fas fa-broadcast-tower"></i> Nu Live</h2>
          {nowPlaying?.track ? (
            <div className="now-playing-info">
              {nowPlaying.track.image_url && (
                <img src={nowPlaying.track.image_url} alt="Album Art" className="np-album-art" />
              )}
              <div className="np-details">
                <h3>{nowPlaying.track.title}</h3>
                <p className="np-artist">{nowPlaying.track.artist || 'Onbekend'}</p>
                {nowPlaying.track.album && (
                  <p className="np-album"><i className="fas fa-compact-disc"></i> {nowPlaying.track.album}</p>
                )}
                {nowPlaying.track.genre && (
                  <p className="np-genre"><i className="fas fa-tag"></i> {nowPlaying.track.genre}</p>
                )}
                {nowPlaying.track.duration && (
                  <p className="np-duration"><i className="fas fa-hourglass-half"></i> {formatDuration(nowPlaying.track.duration)}</p>
                )}
                <div className="np-meta">
                  <span className="live-badge">
                    <i className="fas fa-circle"></i> {nowPlaying.isTTS ? 'DJ AAN HET WOORD' : 'LIVE'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="no-track">Geen track speelt momenteel</p>
          )}
        </div>

        {/* Next Track */}
        <div className="upcoming-card">
          <h2><i className="fas fa-forward"></i> Volgend Nummer</h2>
          {nextTrack ? (
            <div className="upcoming-track-info">
              {nextTrack.image_url && (
                <img src={nextTrack.image_url} alt="Next Album Art" className="np-album-art np-album-art--small" />
              )}
              <div className="np-details">
                <h3>{nextTrack.title}</h3>
                <p className="np-artist">{nextTrack.artist || 'Onbekend'}</p>
                {nextTrack.album && (
                  <p className="np-album"><i className="fas fa-compact-disc"></i> {nextTrack.album}</p>
                )}
                {nextTrack.genre && (
                  <p className="np-genre"><i className="fas fa-tag"></i> {nextTrack.genre}</p>
                )}
                {nextTrack.duration && (
                  <p className="np-duration"><i className="fas fa-hourglass-half"></i> {formatDuration(nextTrack.duration)}</p>
                )}
              </div>
            </div>
          ) : (
            <p className="no-track">Volgende track wordt geladen...</p>
          )}
        </div>
      </div>

      {/* Announcement + Schedule + TTS Queue row */}
      <div className="radio-info-grid">

        {/* Next Announcement */}
        <div className="info-card announcement-card">
          <h2><i className="fas fa-microphone-alt"></i> Volgende Aankondiging</h2>
          {nowPlaying?.nextAnnouncementReady ? (
            <div className="announcement-ready">
              <span className="badge badge--green"><i className="fas fa-check"></i> Klaar</span>
              {nowPlaying.nextAnnouncement && (
                <blockquote className="announcement-text">
                  "{nowPlaying.nextAnnouncement}"
                </blockquote>
              )}
            </div>
          ) : nowPlaying?.isPreparingAnnouncement ? (
            <div className="announcement-preparing">
              <span className="badge badge--orange"><i className="fas fa-spinner fa-spin"></i> AI genereert...</span>
              <p className="info-hint">De AI schrijft een aankondiging voor het volgende nummer</p>
            </div>
          ) : (
            <p className="no-track">
              {nowPlaying ? 'Aankondiging wordt voorbereid...' : 'Laden...'}
            </p>
          )}
        </div>

        {/* Current Schedule */}
        <div className="info-card schedule-card">
          <h2><i className="fas fa-calendar-alt"></i> Huidig Programma</h2>
          {schedule ? (
            <div className="schedule-info">
              <div className="schedule-name">{schedule.name}</div>
              <div className="schedule-details">
                <div className="schedule-detail-item">
                  <i className="fas fa-volume-up"></i>
                  <span>Stem: <strong>{getVoiceLabel(schedule.voice_id)}</strong></span>
                </div>
                <div className="schedule-detail-item">
                  <i className={`fas fa-newspaper ${schedule.auto_news_enabled ? 'text-green' : 'text-grey'}`}></i>
                  <span>
                    Nieuws: <strong>
                      {schedule.auto_news_enabled
                        ? (schedule.news_broadcast_times?.length > 0
                          ? schedule.news_broadcast_times.join(', ')
                          : (schedule.auto_news_interval ? `Elke ${schedule.auto_news_interval} min` : 'Aan'))
                        : 'Uit'}
                    </strong>
                  </span>
                </div>
                <div className="schedule-detail-item">
                  <i className={`fas fa-cloud-sun ${schedule.auto_weather_enabled ? 'text-green' : 'text-grey'}`}></i>
                  <span>
                    Weer: <strong>
                      {schedule.auto_weather_enabled && schedule.weather_broadcast_times?.length > 0
                        ? schedule.weather_broadcast_times.join(', ')
                        : 'Uit'}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="no-track">Geen actief programma</p>
          )}
        </div>

        {/* TTS Queue */}
        <div className="info-card tts-queue-card">
          <h2><i className="fas fa-list-ol"></i> TTS Wachtrij</h2>
          <div className="tts-queue-count">
            <span className={`queue-badge ${ttsQueue.queueLength > 0 ? 'queue-badge--active' : 'queue-badge--empty'}`}>
              {ttsQueue.queueLength}
            </span>
            <span className="queue-label">
              {ttsQueue.queueLength === 0 ? 'Wachtrij leeg' : `bericht${ttsQueue.queueLength !== 1 ? 'en' : ''} in wachtrij`}
            </span>
          </div>
          {ttsQueue.queue && ttsQueue.queue.length > 0 && (
            <ol className="tts-queue-list">
              {ttsQueue.queue.slice(0, 3).map((item, i) => (
                <li key={i} className="tts-queue-item">
                  <span className="tts-queue-text">"{item.text.substring(0, 60)}{item.text.length > 60 ? '...' : ''}"</span>
                </li>
              ))}
              {ttsQueue.queue.length > 3 && (
                <li className="tts-queue-more">+ {ttsQueue.queue.length - 3} meer...</li>
              )}
            </ol>
          )}
        </div>

      </div>
    </div>
  );
};

export default DashboardHome;

